/**
 * Utilities for working with phone numbers
 */

/**
 * Clean phone number (remove all non-digit characters)
 * @param {string} phone - Phone number
 * @returns {string} - Cleaned phone number
 */
const cleanPhone = (phone) => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

/**
 * Format phone number for display (Russian format)
 * @param {string} phone - Phone number
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';

  const cleaned = cleanPhone(phone);

  // Russian mobile: +7 (XXX) XXX-XX-XX
  if (cleaned.length === 11 && cleaned.startsWith('7')) {
    return `+7 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9, 11)}`;
  }

  // Russian mobile without country code: (XXX) XXX-XX-XX
  if (cleaned.length === 10) {
    return `+7 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 8)}-${cleaned.slice(8, 10)}`;
  }

  // St. Petersburg landline: +7 (812) XXX-XX-XX
  if (cleaned.length === 11 && cleaned.startsWith('7812')) {
    return `+7 (812) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9, 11)}`;
  }

  // International format
  if (cleaned.length > 10) {
    return `+${cleaned}`;
  }

  // Fallback: return as is with spaces
  return phone;
};

/**
 * Get tel: link for calling
 * @param {string} phone - Phone number
 * @returns {string} - tel: link
 */
export const getPhoneLink = (phone) => {
  if (!phone) return '';

  const cleaned = cleanPhone(phone);

  // Handle Russian numbers
  if (cleaned.startsWith('8') && cleaned.length === 11) {
    // Convert 8XXXXXXXXXX to +7XXXXXXXXXX
    return `tel:+7${cleaned.slice(1)}`;
  }

  if (cleaned.startsWith('7') && cleaned.length === 11) {
    // Already has country code 7
    return `tel:+${cleaned}`;
  }

  if (cleaned.length === 10) {
    // Missing country code, add +7
    return `tel:+7${cleaned}`;
  }

  // International format
  return `tel:+${cleaned}`;
};

/**
 * Validate phone number (basic validation)
 * @param {string} phone - Phone number
 * @returns {boolean} - True if valid
 */
export const isValidPhone = (phone) => {
  if (!phone) return false;

  const cleaned = cleanPhone(phone);

  // Russian numbers should be 10 or 11 digits
  return cleaned.length >= 10 && cleaned.length <= 15;
};

/**
 * Format phone number for WhatsApp link
 * @param {string} phone - Phone number
 * @returns {string} - WhatsApp URL
 */
export const getWhatsAppLink = (phone) => {
  if (!phone) return '';

  const cleaned = cleanPhone(phone);
  let whatsappNumber = cleaned;

  // Ensure it starts with country code
  if (cleaned.startsWith('8')) {
    whatsappNumber = '7' + cleaned.slice(1);
  } else if (!cleaned.startsWith('7')) {
    whatsappNumber = '7' + cleaned;
  }

  return `https://wa.me/${whatsappNumber}`;
};

/**
 * Format phone number for Telegram link
 * @param {string} phone - Phone number
 * @returns {string} - Telegram URL
 */
export const getTelegramLink = (phone) => {
  if (!phone) return '';

  const cleaned = cleanPhone(phone);
  let telegramNumber = cleaned;

  // Ensure it starts with country code
  if (cleaned.startsWith('8')) {
    telegramNumber = '7' + cleaned.slice(1);
  } else if (!cleaned.startsWith('7')) {
    telegramNumber = '7' + cleaned;
  }

  return `https://t.me/+${telegramNumber}`;
};
